import React, { useContext } from "react";
import { Backdrop, useTheme } from "@mui/material";
import PageContext from "../../components/Common/PageContext";
import { MemorizedManualReceiveEdit } from "../../components/Receive/ManualReceiveEdit";
import { Modal } from "../../components/Modal/Modal";
import { LoadingDialog } from "../../components/Loading/LoadingDialog";
import { ManualHeaderDetails } from "../../components/Invoice/ManualHeaderDetails";
import PreviousReceiveList from "../../components/Receive/PreviousReceiveList";
import TabGroup from "../../components/Tab/TabGroup";
import SpeedDialMenu from "../../components/SpeedDial/SpeedDialMenu";
import CommercialSelect from "../../components/Select/CommercialSelect";
import ModalReceiveFinalise from "../../components/Modal/ModalReceiveFinalise";
import ModalAssignLocation from "../../components/Modal/ModalAssignLocation";
import ModalForcePartReceive from "../../components/Modal/ModalForcePartReceive";
import AlertModal from "../../components/AlertModal/AlertModal";
import { ReceiveHistory } from "../../components/History/ReceiveHistory";
import { ModalHistory } from "../../components/Modal/ModalHistory";
import alertTypeEnum from "../../util/alertTypeEnum";
import modalSize from "../../util/modalSizeEnum";
import { EdiLikeAManualReceivingProps } from "./types";
import { useInitialization } from "./hooks/useInitialization";
import { useReceiveOperations } from "./hooks/useReceiveOperations";
import { useScan } from "./hooks/useScan";
import { useModal } from "./hooks/useModal";
import {
	mapReceivesWithAdditionalDetails,
	receiveType,
	receiveStatus,
	canBeFinalisedStatusList,
	closedStatusList,
	addOrUpdateReceiveToReceivesList,
} from "./utils/receiveUtilExports";
import { speedDialAction } from "../../util/speedDialUtil";
import { MemorizedReadonlyManualReceiveLineItemTable } from "../../components/Invoice/ManualReceiveLineItemTable";
import { useComments } from "./hooks/useComments";
import { CommentModal } from "../../components/Modal/CommentModal";

const EdiLikeAManualReceiving: React.FC<Omit<EdiLikeAManualReceivingProps, 'theme'>> = () => {
	const theme = useTheme();
	const [pageContext] = useContext(PageContext);

	const {
		pageData,
		loadingDialog,
		setLoadingDialog,
		receive,
		setReceive,
		receives,
		setReceives,
		originalReceives,
		locationBays,
		purchaseOrderLineItems,
		activeTab,
		setActiveTab,
		canPurchaseOrderBeReopened,
		enrichedReceive,
		productDetails,
		isPartReceiveFeatureEnabled,
		enableCommercialAccountSelection,
		enabledArrivals,
		enabledPriorityStock,
		captureFreightCostEnabled,
		enableAssignIbLocation,
		enableProductLabelAutoPrint,
		hasLabels,
		returnToHome,
		setModal,
		modal,
		alertHeader,
		setAlertHeader,
		isActionPrevented,
		setIsActionPrevented,
		editableInvoiceFields,
		handleInvoiceFieldChanged,
		handlePreviousReceiveFieldChanged,
		welcomeModalShownThisSession,
		initialised,
		currentScannedProduct,
		setCurrentScannedProduct,
		selectedCommercialAccount,
		setSelectedCommercialAccount,
		inboundLocation,
		setInboundLocation,
	} = useInitialization(receiveType.ediLikeAManual);

	const {
		isExpanded,
		setIsExpanded,
		isHardwareView,
		onReceivedQuantityChange,
		onCostChange,
		onPreviousReceiveQuantityChanged,
		onPreviousReceiveCostChange,
		upsertReceive,
		onExpandHeaderChange,
		onInvoiceExpandChange,
		toggleView,
		onCloseModal,
		onSetInProgressReceive,
		clearCurrentReceive,
	} = useReceiveOperations(
		activeTab,
		receive,
		setReceive,
		receives,
		setReceives,
		pageData?.purchaseOrder,
		setModal,
		setLoadingDialog,
		pageData?.priorityStock ?? [],
		enabledPriorityStock,
		enabledArrivals,
		setAlertHeader,
		setCurrentScannedProduct
	);

	const {
		isModalReceiveFinaliseOpen,
		isModalForcePartReceiveOpen,
		isModalAsnDetailsOpen,
		isBackdropOpen,
		isSpeedDialOpen,
		alertModal,
		assignLocation,
		setAssignLocation,
		onOpenAsnDetailsModal,
		onCloseAsnDetailsModal,
		onOpenSpeedDial,
		onCloseSpeedDial,
		checkInvoiceNumberIsValidAsync,
		onSaveReceiveClick,
		confirmFinalise,
		confirmPartReceive,
		finalisePartReceive,
		finaliseReceive,
		checkForEdi,
		onReopenPoClick,
		onReceiveEditClick,
		onClickClearReceive,
		onLocationNameChange,
		onLocationSubmit,
		receiveHistory,
		checkReceiveHistoryClick,
		onReceiveHistoryClick,
		onCloseModalReceiveFinalise,
		onCloseAlertModal,
		onCloseModalForcePartReceive,
		closingHistoryModal,
	} = useModal(
		pageData,
		activeTab,
		receive,
		receives,
		productDetails,
		originalReceives,
		setModal,
		setLoadingDialog,
		setReceive,
		setReceives,
		setActiveTab,
		upsertReceive,
		onCloseModal,
		returnToHome,
		onSetInProgressReceive,
		clearCurrentReceive,
		setIsExpanded,
		welcomeModalShownThisSession,
		isPartReceiveFeatureEnabled,
		receiveType.ediLikeAManual,
		initialised,
		setIsActionPrevented,
		editableInvoiceFields,
		selectedCommercialAccount
	);

	const { onSaveItemClick, onDataChange } = useScan({
		purchaseOrder: pageData?.purchaseOrder,
		receiveType: receiveType.ediLikeAManual,
		receive,
		productDetails,
		invoiceFields: editableInvoiceFields,
		receives,
		setReceives,
		setReceive,
		onReceivedQuantityChange,
		setCurrentScannedProduct,
		inboundLocation,
		locationBays,
		activeTab,
		assignLocation,
		setModal,
		setIsExpanded,
		setInboundLocation,
		setAssignLocation,
		setActiveTab,
		onCloseModal,
		setLoadingDialog,
		enableAssignIbLocation,
		selectedCommercialAccount,
	});

	const { comments, commentModal, onCommentModalOpen, onCommentModalClose, onAddComment } = useComments(
		receive?.comments || []
	);
	const { commercialAccounts: accounts, userLocation } = pageContext;

	return (
		<>
			{pageData && (
				<>
					<ManualHeaderDetails
						isExpanded={isExpanded.header}
						onExpandClick={onExpandHeaderChange}
						purchaseOrder={pageData.purchaseOrder}
						showLock={false}
						alert={alertHeader}
					/>
					{enableCommercialAccountSelection &&
						accounts?.length > 0 &&
						userLocation === parseInt(pageData.purchaseOrder.shipToLocation.locationId) && (
							<CommercialSelect
								id="Commerical Account"
								menuItems={accounts}
								onChange={setSelectedCommercialAccount}
								currentValue={selectedCommercialAccount}
							/>
						)}

					<TabGroup
						handleTabChange={setActiveTab}
						activeTab={activeTab}
						tabPanels={[
							{
								label: "Details",
								child: (
									<MemorizedReadonlyManualReceiveLineItemTable
										lineItems={purchaseOrderLineItems}
										isHardwareView={isHardwareView}
										showDetailsOnly={true}
										showOverReceive={false}
										isLocationBayEditable={false}
										onLocationNameChange={() => {}}
										priorityStock={pageData.priorityStock || []}
										isPriorityStockEnabled={enabledPriorityStock}
										isArrivalsFeatureEnabled={enabledArrivals}
									/>
								),
							},
							{
								label: "Receive",
								child: (
									<>
										<MemorizedManualReceiveEdit
											validateInvoiceNumber={checkInvoiceNumberIsValidAsync}
											receive={enrichedReceive}
											storeName={pageData.purchaseOrder.shipToLocation.companyName}
											editableInvoiceFields={editableInvoiceFields}
											handleInvoiceFieldChanged={handleInvoiceFieldChanged}
											currentScannedProduct={currentScannedProduct}
											onExpandInvoiceClick={onInvoiceExpandChange}
											isInvoiceExpanded={isExpanded.invoiceDetails}
											onReceivedQuantityChange={(sku, qty) => {
												if (!receive) return;

												const updatedReceive = onReceivedQuantityChange({ receive, sku, newQuantity: qty });
												setReceive(updatedReceive);
												setReceives(addOrUpdateReceiveToReceivesList(receives, updatedReceive));
											}}
											onCostChange={(sku, unitInc, unitEx, lineInc, lineEx) => {
												if (!receive) return;

												const updatedReceive = onCostChange({
													receive,
													sku,
													unitIncCost: unitInc,
													unitExCost: unitEx,
													lineIncCost: lineInc,
													lineExCost: lineEx,
												});
												setReceive(updatedReceive);
												setReceives(addOrUpdateReceiveToReceivesList(receives, updatedReceive));
											}}
											onDataChange={onDataChange}
											onSaveItemClick={onSaveItemClick}
											isHardwareView={isHardwareView}
											onLocationNameChange={onLocationNameChange}
											currentLocation={inboundLocation}
											locationBays={locationBays}
											enableAssignIbLocation={enableAssignIbLocation}
											priorityStock={pageData.priorityStock || []}
											isPriorityStockEnabled={enabledPriorityStock}
											isArrivalsFeatureEnabled={enabledArrivals}
											captureFreightCostEnabled={captureFreightCostEnabled}
											asns={pageData.advancedShippingNotices ?? []}
											isModalAsnDetailsOpen={isModalAsnDetailsOpen}
											onAsnDetailsModalOpen={onOpenAsnDetailsModal}
											onAsnDetailsModalClose={onCloseAsnDetailsModal}
										/>
									</>
								),
							},
							{
								label: "Pending EDI",
								child: (
									<PreviousReceiveList
										receives={mapReceivesWithAdditionalDetails(receives, pageData.purchaseOrder, productDetails)}
										onReceiveEditClick={onReceiveEditClick}
										onReceivedQuantityChange={(receiveId, sku, qty) => {
											onPreviousReceiveQuantityChanged({ receiveId, sku, newQuantity: qty });
										}}
										onCostChange={(receiveId, sku, unitInc, unitEx, lineInc, lineEx) =>
											onPreviousReceiveCostChange({
												receiveId,
												sku,
												unitIncCost: unitInc,
												unitExCost: unitEx,
												lineIncCost: lineInc,
												lineExCost: lineEx,
											})
										}
										showDetailsOnly={false}
										isHardwareView={isHardwareView}
										onSetReceiveProperty={handlePreviousReceiveFieldChanged}
										clearReceive={onClickClearReceive}
										onHistoryClick={onReceiveHistoryClick}
									/>
								),
							},
						]}
					/>

					{assignLocation.isModalOpen && (
						<ModalAssignLocation
							isOpen={assignLocation.isModalOpen}
							onSubmit={onLocationSubmit}
							onCancel={() => setAssignLocation({ ...assignLocation, isModalOpen: false })}
							poLocation={pageData.purchaseOrder.shipToLocation.locationId}
							scannedInboundLocation={assignLocation.scannedLocation}
							currentLocation={assignLocation.currentLocation}
							sku={assignLocation.sku}
							context={assignLocation.context as "manual" | "scanned"}
						/>
					)}

					{receive && isModalReceiveFinaliseOpen && (
						<ModalReceiveFinalise
							isOpen={isModalReceiveFinaliseOpen}
							hasExistingCredit={false}
							skipAllocation={false} // Would be calculated
							onFinaliseSubmit={finaliseReceive}
							onCancelFinalise={onCloseModalReceiveFinalise}
							receive={receive}
							locationId={pageData.purchaseOrder.shipToLocation.locationId}
							originalReceive={originalReceives.find((r) => r.id === receive.id)}
							hasLabels={enableProductLabelAutoPrint && hasLabels}
							showInvoiceTotals
							showFreightCost={captureFreightCostEnabled}
						/>
					)}

					<ModalForcePartReceive
						message="Are you sure you want to force a part receive?"
						buttonText="Yes"
						title="Force part receive?"
						isOpen={isModalForcePartReceiveOpen}
						isFullyReceived={false}
						onFinaliseSubmit={finalisePartReceive}
						onCancelFinalise={onCloseModalForcePartReceive}
					/>

					<ModalHistory
						title="Receive History"
						alertType={alertTypeEnum.INFO}
						primaryButtonName="Ok"
						onPrimaryButtonClick={closingHistoryModal}
						isOpen={receiveHistory.isHistoryVisible}
						onClose={closingHistoryModal}
						children={
							<ReceiveHistory
								statusHistoryList={receiveHistory ? (receiveHistory.statusHistoryList ?? []) : []}
								invoiceNumber={receiveHistory ? (receiveHistory.invoiceNumber ?? "") : ""}
								sku={receiveHistory.sku}
							/>
						}
						size={modalSize.FULLSCREEN}
					/>
				</>
			)}

			<SpeedDialMenu
				isOpen={isSpeedDialOpen}
				onOpen={onOpenSpeedDial}
				onClose={onCloseSpeedDial}
				actions={[
					{
						title: speedDialAction.save,
						onClick: onSaveReceiveClick,
						isDisplay: activeTab === 1 && receive?.status !== receiveStatus.open && !isActionPrevented,
					},
					{
						title: speedDialAction.forcePartReceive,
						onClick: confirmPartReceive,
						isDisplay:
							activeTab === 1 &&
							canBeFinalisedStatusList.includes(receive?.status ?? "") &&
							!isActionPrevented &&
							isPartReceiveFeatureEnabled,
					},
					{
						title: speedDialAction.finalise,
						onClick: confirmFinalise,
						isDisplay:
							activeTab === 1 && canBeFinalisedStatusList.includes(receive?.status ?? "") && !isActionPrevented,
					},
					{
						title: isHardwareView ? speedDialAction.hardwareView : speedDialAction.softwareView,
						onClick: toggleView,
						isDisplay: true,
					},
					{
						title: speedDialAction.checkForEdi,
						onClick: checkForEdi,
						isDisplay: activeTab === 1 && !closedStatusList.includes(receive?.status ?? "") && !!receive?.invoiceNumber,
					},
					{
						title: speedDialAction.clearReceive,
						onClick: onClickClearReceive,
						isDisplay: receive?.status === receiveStatus.inProgress && activeTab === 1,
					},
					{
						title: speedDialAction.history,
						onClick: checkReceiveHistoryClick,
						isDisplay: activeTab === 1 && (receive?.statusHistory ?? []).length > 0,
					},
					{
						title: speedDialAction.reopenPurchaseOrder,
						onClick: onReopenPoClick,
						isDisplay: canPurchaseOrderBeReopened,
					},
					{
						title: speedDialAction.comments,
						onClick: onCommentModalOpen,
						isDisplay: activeTab === 1 && receive,
					},
				]}
			/>

			<LoadingDialog message={loadingDialog.message} isOpen={loadingDialog.isOpen} />
			<Backdrop open={isBackdropOpen} style={theme.backDrop} />

			<Modal {...modal} />

			<CommentModal
				isOpen={commentModal.isOpen}
				isDisabled={!receive?.id}
				onClose={onCommentModalClose}
				comments={comments}
				onAddComment={onAddComment(receive?.id ?? "")}
			/>

			{alertModal.isOpen && (
				<AlertModal
					isOpen={alertModal.isOpen}
					title={alertModal.title}
					status={alertModal.status as "warning" | "error" | "info" | "success"}
					onClose={onCloseAlertModal}
				/>
			)}
		</>
	);
};

export default EdiLikeAManualReceiving;
